import { NsKey } from "enums/NsKey";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation } from "react-router-dom";
import { DashboardMenuItemTooltip } from "./DashboardMenuItemTooltip";
import { PendingValidationsMenuBadge } from "features/validations/components/PendingValidationsMenuBadge";

export interface MenuItem {
  id: string;
  to?: string;
  label: string;
  permission?: string;
  icon: React.ComponentType<{ className?: string }>;
  isActive?: boolean;
  children?: MenuItem[];
  /** Parcours courtier : masqué dans l'espace compagnie (PM de nature Compagnie
   *  ou casquette Plateforme), sauf capability PlateformePortfolioLists. */
  courtierOnly?: boolean;
  /** Espace compagnie : PM de nature Compagnie, ou courtier-dirigeant sous
   *  casquette Plateforme (sa PM est la PM principale d'une plateforme). */
  compagnieOnly?: boolean;
  /** Counter shown on the entry (fetched by a dedicated badge component). */
  badge?: 'pendingValidations';
}

type DashboardMenuItemProps = {
  item: MenuItem;
  isCollapsed: boolean;
  isHovered: boolean;
  onHover: (id: string | null) => void;
  level?: number;
  /** Imposé par le parent : seul l'enfant au match le plus spécifique est actif. */
  forcedActive?: boolean;
};

const matchesPath = (pathname: string, to?: string) =>
  !!to && (pathname === to || pathname.startsWith(to + '/'));

export const DashboardMenuItem: React.FC<DashboardMenuItemProps> = ({
  item,
  isCollapsed,
  isHovered,
  onHover,
  level = 0,
  forcedActive
}) => {
  const { t } = useTranslation(NsKey.Common);
  const location = useLocation();
  const Icon = item.icon;
  const [isOpen, setIsOpen] = useState(false);

  const hasChildren = item.children && item.children.length > 0;

  // Enfant actif = celui au match le plus spécifique (évite qu'un enfant "préfixe"
  // d'un autre — ex. /prelevements vs /prelevements/hbs — soit marqué actif à tort)
  const activeChild = item.children?.reduce<MenuItem | undefined>((best, child) => {
    if (!matchesPath(location.pathname, child.to)) return best;
    return !best || (child.to?.length ?? 0) > (best.to?.length ?? 0) ? child : best;
  }, undefined);
  const hasActiveChild = !!activeChild;

  const isItemActive = forcedActive !== undefined
    ? forcedActive
    : matchesPath(location.pathname, item.to);
  const isActive = isItemActive || hasActiveChild;

  // Ouvre automatiquement le parent si un enfant est actif
  useEffect(() => {
    if (hasActiveChild && !isCollapsed) {
      setIsOpen(true);
    }
  }, [hasActiveChild, isCollapsed]);

  const handleClick = (e: React.MouseEvent) => {
    if (hasChildren && !item.to) {
      e.preventDefault();
      setIsOpen(!isOpen);
    }
  };

  const badge = item.badge === 'pendingValidations'
    ? <PendingValidationsMenuBadge isCollapsed={isCollapsed} />
    : null;

  // Rendered through a function call (not as a nested component) so that the
  // badge keeps its state and does not re-fetch on every hover re-render.
  const renderMenuContent = () => (
    <>
      <div className="relative">
        <Icon className={`
          w-5 h-5 transition-transform duration-200
          ${isHovered && !isActive ? 'scale-110' : ''}
        `} />
        {isActive && level === 0 && (
          <div className="absolute -inset-1 bg-white/20 rounded-lg blur-sm" />
        )}
        {isCollapsed && badge}
      </div>
      {!isCollapsed && (
        <>
          <span className="ml-3 font-medium text-sm tracking-wide">
            {t(item.label)}
          </span>
          {badge && <span className="ml-auto pl-2">{badge}</span>}
          {hasChildren ? (
            <ChevronDown className={`
              ${badge ? 'ml-2' : 'ml-auto'} w-4 h-4 transition-transform duration-200
              ${isOpen ? 'rotate-180' : ''}
            `} />
          ) : (
            <ChevronRight className={`
              ${badge ? 'ml-2' : 'ml-auto'} w-4 h-4 transition-transform duration-200
              ${isHovered ? 'translate-x-1' : ''}
              ${isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}
            `} />
          )}
        </>
      )}
    </>
  );

  const baseClasses = `
    flex items-center p-3 rounded-xl transition-all duration-200 group
    ${isCollapsed ? 'justify-center' : 'justify-start'}
    ${level > 0 ? 'ml-4 text-sm' : 'w-full'}
  `;

  const activeClasses = isActive
    ? 'bg-white/20 text-white'
    : 'hover:bg-white/10 text-white/70 hover:text-white';

  return (
    <div className="relative">
      <div
        onMouseEnter={() => onHover(item.id)}
        onMouseLeave={() => onHover(null)}
      >
        {item.to ? (
          <NavLink
            to={item.to}
            onClick={handleClick}
            className={`${baseClasses} ${activeClasses}`}
          >
            {renderMenuContent()}
          </NavLink>
        ) : (
          <button
            onClick={handleClick}
            className={`${baseClasses} ${activeClasses}`}
          >
            {renderMenuContent()}
          </button>
        )}

        {/* Tooltip pour le mode collapsed */}
        {isCollapsed && isHovered && hasChildren && (
          <div className="absolute left-full ml-2 top-0 z-50 bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg whitespace-nowrap">
            <div className="font-medium mb-2">{t(item.label)}</div>
            <div className="space-y-1">
              {item.children!.map((child) => (
                <NavLink
                  key={child.id}
                  to={child.to!}
                  className="block text-sm text-white/70 hover:text-white py-1"
                >
                  {t(child.label)}
                </NavLink>
              ))}
            </div>
          </div>
        )}

        {/* Tooltip simple pour les items sans children */}
        {isCollapsed && isHovered && !hasChildren && (
          <DashboardMenuItemTooltip
            label={t(item.label)}
            isVisible={true}
          />
        )}
      </div>

      {/* Sous-menus */}
      {!isCollapsed && hasChildren && isOpen && (
        <div className="mt-1 space-y-1">
          {item.children!.map((child) => (
            <DashboardMenuItem
              key={child.id}
              item={child}
              isCollapsed={isCollapsed}
              isHovered={isHovered}
              onHover={onHover}
              level={level + 1}
              forcedActive={child.id === activeChild?.id}
            />
          ))}
        </div>
      )}
    </div>
  );
};
